// Daily Log (By tech / By job; daily, weekly and monthly): date navigation, search, the per-tech
// and per-job rows with hours, and the empty state. Split out of app.js (2026-10-03). The two
// app.js helpers it calls come in through setDailyLogDeps() (see estimates.js for why it doesn't
// import app.js back). The Daily Log menu button and closedJobList() stay in app.js.
import { techLabel } from './techNames';
import { getDB } from './utilities';

let shell = null;
export function setDailyLogDeps(deps) { shell = deps; }

function getActiveView() {
  const active = document.querySelector('.active-log-view');
  return active ? active.getAttribute('data-view') : 'daily';
}

export function formatYMDLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function parseYMDLocal(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function shiftDateByViewYMD(baseYMD, view, dir) {
  const d = parseYMDLocal(baseYMD || formatYMDLocal(new Date()));
  if (view === 'weekly') d.setDate(d.getDate() + 7 * dir);   // same weekday next/prev week
  else if (view === 'monthly') d.setMonth(d.getMonth() + dir);
  else d.setDate(d.getDate() + dir);
  return formatYMDLocal(d);
}


document.getElementById('daily-log-date-next').addEventListener('click', async function () {
  const input = document.getElementById('daily-log-date');
  const view = getActiveView();
  const currentYMD = input.value || formatYMDLocal(new Date());
  input.value = shiftDateByViewYMD(currentYMD, view, +1);
  loadDailyLog(view);
});

document.getElementById('daily-log-date-previous').addEventListener('click', async function () {
  const input = document.getElementById('daily-log-date');
  const view = getActiveView();
  const currentYMD = input.value || formatYMDLocal(new Date());
  input.value = shiftDateByViewYMD(currentYMD, view, -1);
  loadDailyLog(view);
});


document.getElementById('daily-log-date').addEventListener('change', async function() {
  const view = getActiveView();
  loadDailyLog(view);
});

async function resolveDeferredProps(data) {
  if (data && typeof data === 'object') {
    await Promise.all(
      Object.entries(data).map(async ([k, v]) => {
        if (v instanceof Promise) data[k] = await v;
      })
    );
  }
}

function ymdToLocalMs(s) {
  if (!s) return NaN;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return NaN;
  return new Date(y, m - 1, d).getTime();
}
function ymdToLocalDate(s) {
  const ms = ymdToLocalMs(s);
  return isNaN(ms) ? null : new Date(ms);
}


export async function loadDailyLog(view) {
  try {
    const db = await getDB();

    const jobStore = db.transaction('jobData', 'readonly').objectStore('jobData');

    const jobList = await new Promise((resolve, reject) => {
      const r = jobStore.getAll();
      r.onsuccess = () => resolve(r.result ?? []);
      r.onerror = () => reject(r.error);
    });

    const jobs = (jobList ?? []).filter(
      job => job && typeof job === 'object' && job.id !== 'lastUpdated'
    );

    const resolvedJobList = [];

    for (let i = 0; i < jobs.length; i++) {
      const job = jobs[i];

      job.Labor = job.Labor && typeof job.Labor === 'object' ? job.Labor : {};
      job.Parts = job.Parts && typeof job.Parts === 'object' ? job.Parts : {};

      await resolveDeferredProps(job.Labor);
      await resolveDeferredProps(job.Parts);

      for (const k in job.Labor) {
        const s = job.Labor[k]?.Time_Submitted;
        job.Labor[k].Time_Submitted_ms = ymdToLocalMs(s);
      }

      resolvedJobList.push(job);

      // yield occasionally so mobile doesn't hitch
      if (i % 50 === 0) {
        await new Promise(r => setTimeout(r, 0));
      }
    }

    createDailyLogRows(resolvedJobList, view);
    applyDailyLogSearch();

  } catch (err) {
    console.error('Error fetching job data from IndexedDB:', err);
  }
}


function getInitials(name) {
  if (!name) return '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function round1(n) {
  return Math.round((Number(n) || 0) * 10) / 10;
}

function getDailyStatusPillInfo(status) {
  switch (status) {
    case 'Needs Scheduling': return { cls: 'status-pill-info', label: 'Needs Sched.' };
    case 'On Work Schedule': return { cls: 'status-pill-success', label: 'Scheduled' };
    case 'In Progress': return { cls: 'status-pill-amber', label: 'In Progress' };
    case 'Ready to Bill':
    case 'Complete': return { cls: 'status-pill-danger', label: 'Ready to Bill' };
    case 'Hold': return { cls: 'status-pill-neutral', label: 'Hold' };
    case 'Declined': return { cls: 'status-pill-danger', label: 'Declined' };
    case 'Ready to Frame':
    case 'Vacuumed':
    case 'Framed':
    case 'Covered':
    case 'Tightened': return { cls: 'status-pill-amber', label: status };
    case 'Closed': return { cls: 'status-pill-neutral', label: 'Closed' };
    default: return { cls: 'status-pill-neutral', label: status || '—' };
  }
}

function buildStatusPill(status) {
  const info = getDailyStatusPillInfo(status);
  const pill = document.createElement('span');
  pill.className = `status-pill ${info.cls}`;
  const dot = document.createElement('span');
  dot.className = 'status-pill-dot';
  pill.appendChild(dot);
  pill.appendChild(document.createTextNode(info.label));
  return pill;
}

function formatWeekRangeLabel(startMs, endMs) {
  const monthShort = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const s = new Date(startMs);
  const e = new Date(endMs);
  return s.getMonth() === e.getMonth()
    ? `${monthShort[s.getMonth()]} ${s.getDate()} – ${e.getDate()}`
    : `${monthShort[s.getMonth()]} ${s.getDate()} – ${monthShort[e.getMonth()]} ${e.getDate()}`;
}

function formatWeekDateBadge(ymd) {
  const d = ymdToLocalDate(ymd);
  if (!d) return '';
  const dayShort = ['SUN','MON','TUE','WED','THU','FRI','SAT'];
  return `${dayShort[d.getDay()]} ${d.getDate()}`;
}

function updateDailyLogDateLabel() {
  const input = document.getElementById('daily-log-date');
  const label = document.getElementById('daily-log-date-label');
  if (!input || !label || !input.value) return;
  const d = ymdToLocalDate(input.value);
  if (!d) return;
  const dayShort = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const monthShort = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  label.textContent = `${dayShort[d.getDay()]}, ${monthShort[d.getMonth()]} ${d.getDate()}`;
}

function createTechContainer(tech) {
  const techContainer = document.createElement('div');
  techContainer.className = 'tech-container';
  techContainer.setAttribute('data-tech', tech);

  const techHeader = document.createElement('div');
  techHeader.className = 'tech-header';

  const avatar = document.createElement('div');
  avatar.className = 'tech-avatar';
  avatar.textContent = getInitials(techLabel(tech));
  techHeader.appendChild(avatar);

  const techName = document.createElement('p');
  techName.className = 'tech-name-header';
  techName.textContent = techLabel(tech);
  techHeader.appendChild(techName);

  const techCounts = document.createElement('div');
  techCounts.className = 'tech-counts';

  const numberOfJobs = document.createElement('span');
  numberOfJobs.id = `job-count-${tech}`;
  numberOfJobs.className = 'job-count';

  const numberOfHours = document.createElement('span');
  numberOfHours.id = `hour-count-${tech}`;
  numberOfHours.className = 'hour-count-day hour-count-value';

  const numberOfHoursWeekly = document.createElement('span');
  numberOfHoursWeekly.id = `hour-count-weekly-${tech}`;
  numberOfHoursWeekly.className = 'hour-count-week hour-count-value hide-hours';

  const numberOfHoursMonthly = document.createElement('span');
  numberOfHoursMonthly.id = `hour-count-monthly-${tech}`;
  numberOfHoursMonthly.className = 'hour-count-month hour-count-value hide-hours';

  techCounts.appendChild(numberOfJobs);
  techCounts.appendChild(numberOfHours);
  techCounts.appendChild(numberOfHoursWeekly);
  techCounts.appendChild(numberOfHoursMonthly);

  techHeader.appendChild(techCounts);

  const rowsWrap = document.createElement('div');
  rowsWrap.className = 'tech-rows';

  techContainer.appendChild(techHeader);
  techContainer.appendChild(rowsWrap);

  return { techContainer, rowsWrap };
}

function setDailyRowAttrs(el, job) {
  el.setAttribute('data-id', job.Job_ID);
  el.setAttribute('data-tech', job.Techs);
  el.setAttribute('data-techId', job.Techs !== '' ? shell.getTechId(job.Techs) || '' : '');
  el.setAttribute('data-jobInfo', JSON.stringify(job));
  el.setAttribute('data-customerid', job.Customer_ID);
  el.setAttribute('data-boatid', job.Boat_ID);
}

function opTitle(job) {
  return `${job.OpCode}: ${(job.Short_Description_DM || job.Description || '').replace(/\([^)]*\)/g, '').trim()}`;
}

function buildDetailRow(job, labor, view) {
  const jobDiv = document.createElement('div');
  jobDiv.className = 'daily-item';
  setDailyRowAttrs(jobDiv, job);
  if (view === 'monthly') jobDiv.classList.add('month-view-row', 'show-month-row');

  const mainDiv = document.createElement('div');
  mainDiv.className = 'daily-item-main';
  const opP = document.createElement('div');
  opP.className = 'daily-item-op';
  opP.textContent = opTitle(job);
  const custP = document.createElement('div');
  custP.className = 'daily-item-cust';
  custP.textContent = [job.Customer_Name, job.Boat_Name].filter(Boolean).join(' · ');
  mainDiv.appendChild(opP);
  mainDiv.appendChild(custP);

  const laborP = document.createElement('div');
  laborP.className = 'daily-item-labor';
  laborP.textContent = labor.Description || '';

  const statusWrap = document.createElement('div');
  statusWrap.className = 'daily-item-status';
  statusWrap.appendChild(buildStatusPill(job.Status));

  const partsCount = job.Parts && typeof job.Parts === 'object' ? Object.keys(job.Parts).length : 0;
  const partsP = document.createElement('div');
  partsP.className = 'daily-item-parts';
  partsP.textContent = partsCount > 0 ? `${partsCount} parts` : '—';

  const hoursP = document.createElement('div');
  hoursP.className = 'daily-item-hours';
  hoursP.textContent = labor.Hours;

  jobDiv.appendChild(mainDiv);
  jobDiv.appendChild(laborP);
  jobDiv.appendChild(statusWrap);
  jobDiv.appendChild(partsP);
  jobDiv.appendChild(hoursP);

  return jobDiv;
}

function buildWeekRow(job, labor) {
  const jobDiv = document.createElement('div');
  jobDiv.className = 'daily-item daily-item-week week-view-row show-week-row';
  setDailyRowAttrs(jobDiv, job);

  const dateP = document.createElement('div');
  dateP.className = 'daily-item-date';
  dateP.textContent = formatWeekDateBadge(labor.Time_Submitted);

  const opP = document.createElement('div');
  opP.className = 'daily-item-op-compact';
  opP.textContent = job.Customer_Name ? `${opTitle(job)} — ${job.Customer_Name}` : opTitle(job);

  const hoursP = document.createElement('div');
  hoursP.className = 'daily-item-hours';
  hoursP.textContent = labor.Hours;

  jobDiv.appendChild(dateP);
  jobDiv.appendChild(opP);
  jobDiv.appendChild(hoursP);

  return jobDiv;
}

function buildJobViewRow(job, labor, tech) {
  const rowDiv = document.createElement('div');
  rowDiv.className = 'daily-job-row';
  setDailyRowAttrs(rowDiv, job);

  const chip = document.createElement('span');
  chip.className = 'tech-chip';
  chip.textContent = techLabel(tech);

  const mainDiv = document.createElement('div');
  mainDiv.className = 'daily-item-main';
  const opP = document.createElement('div');
  opP.className = 'daily-item-op';
  opP.textContent = opTitle(job);
  const custP = document.createElement('div');
  custP.className = 'daily-item-cust';
  custP.textContent = [job.Customer_Name, job.Boat_Name].filter(Boolean).join(' · ');
  mainDiv.appendChild(opP);
  mainDiv.appendChild(custP);

  const statusWrap = document.createElement('div');
  statusWrap.className = 'daily-item-status';
  statusWrap.appendChild(buildStatusPill(job.Status));

  const hoursP = document.createElement('div');
  hoursP.className = 'daily-item-hours';
  hoursP.textContent = labor.Hours;

  rowDiv.appendChild(chip);
  rowDiv.appendChild(mainDiv);
  rowDiv.appendChild(statusWrap);
  rowDiv.appendChild(hoursP);

  return rowDiv;
}

// "Week of Sep 27" / "October 2026" / "Saturday, October 3" for the period being shown.
// (Weeks run Monday-Saturday, matching the week view's range in createDailyLogRows.)
function periodLabel(view, anchorYMD, { long = true } = {}) {
  const d = ymdToLocalDate(anchorYMD);
  if (!d) return '';
  if (view === 'weekly') {
    const start = new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7));
    return 'week of ' + start.toLocaleDateString('default', { month: 'short', day: 'numeric' });
  }
  if (view === 'monthly') return d.toLocaleDateString('default', { month: 'long', year: 'numeric' });
  return d.toLocaleDateString('default', long
    ? { weekday: 'long', month: 'long', day: 'numeric' }
    : { weekday: 'short', month: 'short', day: 'numeric' });
}

function updateDailyLogSummary(view, anchorYMD, techCount, entryCount, totalHours) {
  const el = document.getElementById('daily-log-summary');
  if (!el) return;
  if (entryCount === 0) { el.innerHTML = ''; return; }

  const label = periodLabel(view, anchorYMD);

  el.innerHTML = `<span class="daily-log-summary-date">${label.toUpperCase()}</span><span class="daily-log-summary-sep"></span><span class="daily-log-summary-text">${techCount} tech${techCount === 1 ? '' : 's'} · ${entryCount} entr${entryCount === 1 ? 'y' : 'ies'} · <span class="daily-log-summary-hours">${round1(totalHours)} hrs</span></span>`;
}

function toggleDailyLogEmptyState(isEmpty, anchorYMD, view = 'daily') {
  const emptyEl = document.getElementById('daily-log-empty');
  const techListEl = document.getElementById('daily-log-list');
  const jobListEl = document.getElementById('daily-log-job-list');
  if (!emptyEl) return;

  if (isEmpty) {
    const label = periodLabel(view, anchorYMD, { long: false });
    const titleEl = document.getElementById('daily-log-empty-title');
    if (titleEl) titleEl.textContent = view === 'weekly' ? `No labor logged the ${label}`
      : view === 'monthly' ? `No labor logged in ${label}`
      : `No labor logged on ${label}`;
    emptyEl.style.display = 'flex';
    techListEl.style.display = 'none';
    jobListEl.style.display = 'none';
  } else {
    emptyEl.style.display = 'none';
    const jobViewActive = document.getElementById('log-job-view').classList.contains('active-daily-view');
    techListEl.style.display = jobViewActive ? 'none' : 'flex';
    jobListEl.style.display = jobViewActive ? 'flex' : 'none';
  }
}

document.getElementById('daily-log-empty-back-btn')?.addEventListener('click', () => {
  document.getElementById('daily-log-date').value = formatYMDLocal(new Date());
  document.getElementById('daily-view-button').click();
});

document.getElementById('daily-log-today-btn')?.addEventListener('click', () => {
  document.getElementById('daily-log-date').value = formatYMDLocal(new Date());
  loadDailyLog(getActiveView());
});

document.getElementById('daily-log-search-input')?.addEventListener('input', applyDailyLogSearch);

// Also run after every redraw (date, period or view change), so the list always matches the box.
function applyDailyLogSearch() {
  const q = (document.getElementById('daily-log-search-input')?.value || '').trim().toLowerCase();
  document.querySelectorAll('#daily-log-list .daily-item, #daily-log-job-list .daily-job-row').forEach(row => {
    const match = !q || row.textContent.toLowerCase().includes(q);
    row.style.display = match ? '' : 'none';
  });
  document.querySelectorAll('#daily-log-list .tech-container').forEach(container => {
    // A tech's name lives in the header, not the rows: matching it shows all their entries.
    const techMatch = !!q && (container.querySelector('.tech-name-header')?.textContent || '').toLowerCase().includes(q);
    if (techMatch) container.querySelectorAll('.daily-item').forEach(r => { r.style.display = ''; });
    const anyVisible = techMatch || Array.from(container.querySelectorAll('.daily-item')).some(r => r.style.display !== 'none');
    container.style.display = anyVisible ? '' : 'none';
  });
}

function createDailyLogRows(jobs, view) {
  const dailyLogList = document.getElementById('daily-log-list');
  const dailyLogJobList = document.getElementById('daily-log-job-list');

  dailyLogList.innerHTML = '';
  dailyLogJobList.innerHTML = '';

  const techContainers = {};
  const techRowsWrap = {};
  const techList = {};

  const currentDateInput = document.getElementById('daily-log-date');
  if (currentDateInput.value === '') {
    const n = new Date();
    currentDateInput.value = `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`;
  }
  const anchorYMD = currentDateInput.value;
  updateDailyLogDateLabel();

  let windowStartMs = null;
  let windowEndMs = null;

if (view === 'monthly') {
  const d = ymdToLocalDate(anchorYMD);
  const start = new Date(d.getFullYear(), d.getMonth(), 1);
  const end   = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
  windowStartMs = start.setHours(0,0,0,0);
  windowEndMs   = end.getTime();
} else if (view === 'weekly') {
  const d = ymdToLocalDate(anchorYMD);
  const day = d.getDay(); // 0..6
  const mon = new Date(d);
  mon.setDate(d.getDate() - ((day + 6) % 7));  // Monday
  const sat = new Date(mon);
  sat.setDate(mon.getDate() + 5);               // Saturday
  windowStartMs = mon.setHours(0,0,0,0);
  windowEndMs   = new Date(sat.getFullYear(), sat.getMonth(), sat.getDate(), 23, 59, 59, 999).getTime();
} else {
  const day = ymdToLocalDate(anchorYMD);
  windowStartMs = day.setHours(0,0,0,0);
  windowEndMs   = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59, 999).getTime();
}

  let totalEntries = 0;
  let totalHours = 0;
  const techsWithEntries = new Set();

  jobs.forEach((job) => {
    if (!job.Parts && !job.Labor) return;

    if (job.Labor) {
      for (const key in job.Labor) {
        const ms = job.Labor[key].Time_Submitted_ms;
        if (isNaN(ms) || ms < windowStartMs || ms > windowEndMs) delete job.Labor[key];
      }
    }

    if (job.Labor) {
      for (const key in job.Labor) {
        const hours = Number(job.Labor[key].Hours);
        if (isNaN(hours)) continue;
        const tech = job.Labor[key].Tech;
        const date = job.Labor[key].Time_Submitted;
        techList[tech] = techList[tech] || {};
        techList[tech][date] = (techList[tech][date] || 0) + hours;
      }
    }

    const hasLabor = job.Labor && Object.keys(job.Labor).length > 0;
    if (!hasLabor) return;

    for (const key in job.Labor) {
      const labor = job.Labor[key];
      const tech = labor.Tech;

      if (!techContainers[tech]) {
        const { techContainer, rowsWrap } = createTechContainer(tech);
        dailyLogList.appendChild(techContainer);
        techContainers[tech] = techContainer;
        techRowsWrap[tech] = rowsWrap;
      }

      const detailRow = view === 'weekly' ? buildWeekRow(job, labor) : buildDetailRow(job, labor, view);
      detailRow.addEventListener('click', (e) => { shell.opCheck(e, null, true); });
      techRowsWrap[tech].appendChild(detailRow);

      const jobRow = buildJobViewRow(job, labor, tech);
      jobRow.addEventListener('click', (e) => { shell.opCheck(e); });
      dailyLogJobList.appendChild(jobRow);

      totalEntries++;
      totalHours += Number(labor.Hours) || 0;
      techsWithEntries.add(tech);
    }
  });

  for (const tech in techContainers) {
    const jobCount = techRowsWrap[tech].children.length;
    const techData = techList[tech] || {};

   const dayHours = techData[anchorYMD] || 0;

// weekly hours use the same inclusive [windowStartMs, windowEndMs]
const weeklyHours = Object.entries(techData).reduce((sum, [dateStr, val]) => {
  const ms = ymdToLocalMs(dateStr);
  return (!isNaN(ms) && ms >= windowStartMs && ms <= windowEndMs) ? sum + Number(val) : sum;
}, 0);

// monthly hours use inclusive end-of-day too
const [ay, am] = anchorYMD.split('-').map(Number);
const mStartMs = new Date(ay, am - 1, 1, 0, 0, 0, 0).getTime();
const mEndMs   = new Date(ay, am, 0, 23, 59, 59, 999).getTime();

const monthlyHours = Object.entries(techData).reduce((sum, [dateStr, val]) => {
  const ms = ymdToLocalMs(dateStr);
  return (!isNaN(ms) && ms >= mStartMs && ms <= mEndMs) ? sum + Number(val) : sum;
}, 0);


    const jobCountElement = document.getElementById(`job-count-${tech}`);
    const hourCountElement = document.getElementById(`hour-count-${tech}`);
    const weeklyHourCountElement = document.getElementById(`hour-count-weekly-${tech}`);
    const monthlyHourCountElement = document.getElementById(`hour-count-monthly-${tech}`);

    if (!jobCountElement || !hourCountElement || !weeklyHourCountElement || !monthlyHourCountElement) continue;

    jobCountElement.textContent = view === 'weekly'
      ? formatWeekRangeLabel(windowStartMs, windowEndMs)
      : `${jobCount} job${jobCount === 1 ? '' : 's'}`;
    hourCountElement.textContent = `${round1(dayHours)} hrs`;
    weeklyHourCountElement.textContent = `${round1(weeklyHours)} hrs`;
    monthlyHourCountElement.textContent = `${round1(monthlyHours)} hrs`;

    if (view === 'weekly') {
      weeklyHourCountElement.classList.remove('hide-hours');
      hourCountElement.classList.add('hide-hours');
      monthlyHourCountElement.classList.add('hide-hours');
    } else if (view === 'monthly') {
      monthlyHourCountElement.classList.remove('hide-hours');
      hourCountElement.classList.add('hide-hours');
      weeklyHourCountElement.classList.add('hide-hours');
    } else {
      hourCountElement.classList.remove('hide-hours');
      weeklyHourCountElement.classList.add('hide-hours');
      monthlyHourCountElement.classList.add('hide-hours');
    }
  }

  updateDailyLogSummary(view, anchorYMD, techsWithEntries.size, totalEntries, totalHours);
  toggleDailyLogEmptyState(totalEntries === 0, anchorYMD, view);
}


document.getElementById('log-job-view').addEventListener('click', () => {
  document.getElementById('log-job-view').classList.add('active-daily-view');
  document.getElementById('log-tech-view').classList.remove('active-daily-view');
  const techView = document.getElementById('daily-log-list');
  const jobView = document.getElementById('daily-log-job-list');
  techView.style.display = 'none';
  jobView.style.display = 'flex';
});

document.getElementById('log-tech-view').addEventListener('click', () => {
  document.getElementById('log-job-view').classList.remove('active-daily-view');
  document.getElementById('log-tech-view').classList.add('active-daily-view');
  const techView = document.getElementById('daily-log-list');
  const jobView = document.getElementById('daily-log-job-list');
  techView.style.display = 'flex';
  jobView.style.display = 'none';
});

document.getElementById('weekly-view-button').addEventListener('click', () => {
  document.getElementById('daily-view-button').classList.remove('active-log-view');
  document.getElementById('weekly-view-button').classList.add('active-log-view');
  document.getElementById('monthly-view-button')?.classList.remove('active-log-view');
  const weekRows = document.querySelectorAll('.week-view-row');
  weekRows.forEach(row => { row.classList.add('show-week-row'); });
  const monthRows = document.querySelectorAll('.month-view-row');
  monthRows.forEach(row => { row.classList.remove('show-month-row'); });
  const weekHeaders = document.querySelectorAll('.hour-count-week');
  weekHeaders.forEach(header => { header.classList.remove('hide-hours'); });
  const monthHeaders = document.querySelectorAll('.hour-count-month');
  monthHeaders.forEach(header => { header.classList.add('hide-hours'); });
  const dayHeaders = document.querySelectorAll('.hour-count-day');
  dayHeaders.forEach(header => { header.classList.add('hide-hours'); });
  loadDailyLog('weekly');
});

document.getElementById('daily-view-button').addEventListener('click', () => {
  document.getElementById('daily-view-button').classList.add('active-log-view');
  document.getElementById('weekly-view-button').classList.remove('active-log-view');
  document.getElementById('monthly-view-button')?.classList.remove('active-log-view');
  const weekRows = document.querySelectorAll('.week-view-row');
  weekRows.forEach(row => { row.classList.remove('show-week-row'); });
  const monthRows = document.querySelectorAll('.month-view-row');
  monthRows.forEach(row => { row.classList.remove('show-month-row'); });
  const weekHeaders = document.querySelectorAll('.hour-count-week');
  weekHeaders.forEach(header => { header.classList.add('hide-hours'); });
  const monthHeaders = document.querySelectorAll('.hour-count-month');
  monthHeaders.forEach(header => { header.classList.add('hide-hours'); });
  const dayHeaders = document.querySelectorAll('.hour-count-day');
  dayHeaders.forEach(header => { header.classList.remove('hide-hours'); });
  loadDailyLog('daily');
});

document.getElementById('monthly-view-button').addEventListener('click', () => {
  document.getElementById('daily-view-button').classList.remove('active-log-view');
  document.getElementById('weekly-view-button').classList.remove('active-log-view');
  document.getElementById('monthly-view-button').classList.add('active-log-view');
  loadDailyLog('monthly');
});
